/**
 * "Saved to Wishlist" toast.
 *
 * Appears when a product is added to the wishlist, with a link to the wishlist
 * page and a close button. Auto-dismisses.
 *
 * WHY A MUTATION OBSERVER RATHER THAN A CLICK HANDLER
 * assets/wishlist.js owns the wishlist buttons and toggles the
 * `added-to-wishlist` class itself. Adding a competing click handler would race
 * with it, and we would have to duplicate its add/remove logic to know which
 * way the toggle went. Watching for the class instead keeps this file fully
 * decoupled: it reacts to the result, whatever caused it, including buttons
 * rendered later by the product grid, quick view, or a filtered collection.
 *
 * Only additions fire the toast. Removing from the wishlist is silent, which
 * matches the design.
 */
if (!customElements.get("m-wishlist-toast")) {
  const ADDED_CLASS = "added-to-wishlist";

  class MWishlistToast extends HTMLElement {
    connectedCallback() {
      this.hideDelay = Number(this.dataset.hideDelay || 4000);
      this.observeButtons();
      this.bindDismiss();
    }

    observeButtons() {
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type !== "attributes" || m.attributeName !== "class") continue;

          const el = m.target;
          if (!el.classList || !el.classList.contains("m-wishlist-button")) continue;

          const isAdded = el.classList.contains(ADDED_CLASS);
          const wasAdded = (m.oldValue || "").includes(ADDED_CLASS);

          // Only announce a transition into the added state. The element that
          // changed is the button that was clicked, so it doubles as the
          // anchor to position the toast against.
          if (isAdded && !wasAdded) {
            this.show(el);
            break;
          }
        }
      });

      observer.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
        attributeOldValue: true,
      });

      this.observer = observer;
    }

    bindDismiss() {
      addEventDelegate({
        context: this,
        event: "click",
        selector: "[data-wishlist-toast-close]",
        handler: () => this.hide(),
      });
    }

    show(anchor) {
      clearTimeout(this.timer);
      this.positionNear(anchor);
      this.classList.add("is-visible");
      this.setAttribute("aria-hidden", "false");
      this.timer = setTimeout(() => this.hide(), this.hideDelay);
    }

    /**
     * Place the toast directly beneath the heart that was clicked, right edges
     * aligned, rather than parking it in the corner of the viewport. The design
     * shows it attached to the control that produced it.
     *
     * The element is position:fixed, so viewport coordinates from
     * getBoundingClientRect can be used as-is. If there is no usable anchor, or
     * it is scrolled out of view, fall back to the bottom-right corner by
     * clearing the inline offsets and letting the stylesheet win.
     */
    positionNear(anchor) {
      const GAP = 10;

      if (!anchor || typeof anchor.getBoundingClientRect !== "function") {
        return this.resetPosition();
      }

      const rect = anchor.getBoundingClientRect();
      const offscreen =
        rect.bottom < 0 ||
        rect.top > window.innerHeight ||
        (rect.width === 0 && rect.height === 0);
      if (offscreen) return this.resetPosition();

      // Measure while hidden. visibility:hidden still reports a box.
      const self = this.getBoundingClientRect();
      const width = self.width || 240;
      const height = self.height || 44;

      let left = rect.right - width;
      let top = rect.bottom + GAP;

      // Keep it on screen on narrow viewports and near the bottom edge.
      const margin = 12;
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
      if (top + height > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - height - GAP);
      }

      this.classList.add("is-anchored");
      this.style.left = `${Math.round(left)}px`;
      this.style.top = `${Math.round(top)}px`;
      this.style.right = "auto";
      this.style.bottom = "auto";
    }

    resetPosition() {
      this.classList.remove("is-anchored");
      this.style.left = "";
      this.style.top = "";
      this.style.right = "";
      this.style.bottom = "";
    }

    hide() {
      clearTimeout(this.timer);
      this.classList.remove("is-visible");
      this.setAttribute("aria-hidden", "true");
    }
  }

  customElements.define("m-wishlist-toast", MWishlistToast);
}
